import { useState, useEffect, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Drawer } from "../Drawer";
import { Icon } from "../ui/Icon";
import { ConfirmDialog } from "../ui/ConfirmDialog";
import { HardDeleteConfirm } from "../ui/HardDeleteConfirm";
import { trpc } from "../../lib/trpc";

interface Props {
  open: boolean;
  transferId: string | null;
  canCreate: boolean;
  canApprove: boolean;
  isSuperAdmin: boolean;
  onClose: () => void;
}

type IStatus = "pending" | "approved" | "cancelled";
const STATUS_META: Record<IStatus, { cls: string; label: string; icon: string }> = {
  pending: { cls: "b-warn", label: "Onay bekliyor", icon: "clock" },
  approved: { cls: "b-success", label: "Onaylandı", icon: "check" },
  cancelled: { cls: "", label: "İptal", icon: "x" },
};

function fmtQty(n: number): string { return new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 3 }).format(n); }
function fmtDateTime(d: string | Date | null): string {
  return d ? new Intl.DateTimeFormat("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }).format(new Date(d)) : "—";
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <span style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--neutral-500)", fontWeight: 600 }}>{label}</span>
      <span style={{ fontSize: 13 }}>{children}</span>
    </div>
  );
}

/** İç transfer detayı: durum + onayla/iptal/sil + satırlar + (pending) FEFO önizleme / (approved) taşınan lotlar. */
export function InternalTransferDetailDrawer({ open, transferId, canCreate, canApprove, isSuperAdmin, onClose }: Props) {
  const qc = useQueryClient();
  const detQ = trpc.internalTransfer.get.useQuery({ id: transferId ?? "" }, { enabled: open && !!transferId });
  const approveMut = trpc.internalTransfer.approve.useMutation();
  const cancelMut = trpc.internalTransfer.cancel.useMutation();
  const removeMut = trpc.internalTransfer.remove.useMutation();

  const [confirm, setConfirm] = useState<null | "approve" | "cancel">(null);
  const [delOpen, setDelOpen] = useState(false);
  const [actErr, setActErr] = useState<string | null>(null);
  // Kalıcı monte edilir (unmount olmaz) → transfer/açılış değişince aksiyon state'lerini sıfırla (eski hata sızmasın).
  useEffect(() => { setActErr(null); setConfirm(null); setDelOpen(false); }, [transferId, open]);

  const t = detQ.data;
  const status = (t?.status ?? "pending") as IStatus;
  const meta = STATUS_META[status];

  async function doApprove() {
    setActErr(null);
    try { await approveMut.mutateAsync({ id: transferId! }); await qc.invalidateQueries(); setConfirm(null); }
    catch (e) { setActErr((e as Error).message); setConfirm(null); }
  }
  async function doCancel() {
    setActErr(null);
    try { await cancelMut.mutateAsync({ id: transferId! }); await qc.invalidateQueries(); setConfirm(null); }
    catch (e) { setActErr((e as Error).message); setConfirm(null); }
  }
  async function doRemove() {
    setActErr(null);
    try { await removeMut.mutateAsync({ id: transferId! }); await qc.invalidateQueries(); setDelOpen(false); onClose(); }
    catch (e) { setActErr((e as Error).message); }
  }

  const hasShortage = (t?.preview ?? []).some((p) => p.shortage > 0.0001);

  return (
    <Drawer open={open} onClose={onClose} size="xl" title={t ? t.code : "İç Transfer"} subtitle={t ? `${t.fromName} → ${t.toName}` : undefined}
      footer={<>
        <button type="button" className="btn-md ghost" onClick={onClose}>Kapat</button>
        {t && status !== "approved" && isSuperAdmin ? (
          <button type="button" className="btn-md danger-outline" onClick={() => setDelOpen(true)}><Icon name="trash" size={14} />Sil</button>
        ) : null}
        {t && status === "pending" && canCreate ? (
          <button type="button" className="btn-md secondary" onClick={() => setConfirm("cancel")}><Icon name="x" size={14} />İptal Et</button>
        ) : null}
        {t && status === "pending" && canApprove ? (
          <button type="button" className="btn-md primary" onClick={() => setConfirm("approve")} disabled={approveMut.isPending}><Icon name="check" size={14} />Onayla & Taşı</button>
        ) : null}
      </>}>
      {detQ.isLoading || !t ? (
        <div className="mt-empty">Yükleniyor…</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {actErr ? <div className="form-banner-err">{actErr}</div> : null}

          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span className={`badge-pill ${meta.cls}`}><Icon name={meta.icon} size={11} />{meta.label}</span>
            <span style={{ fontSize: 12, color: "var(--neutral-500)" }}>{t.lines.length} ürün satırı</span>
          </div>

          <div className="d-card">
            <div className="d-card-body" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              <Row label="Kaynak Depo">{t.fromName} <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--neutral-400)" }}>{t.fromCode}</span></Row>
              <Row label="Hedef Depo">{t.toName} <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--neutral-400)" }}>{t.toCode}</span></Row>
              <Row label="Talep Eden">{t.requestedBy}</Row>
              <Row label="Talep Tarihi"><span style={{ fontFamily: "var(--font-mono)", fontSize: 12 }}>{fmtDateTime(t.requestedAt)}</span></Row>
              <Row label="Onaylayan">{t.approvedBy ?? "—"}</Row>
              <Row label="Onay Tarihi"><span style={{ fontFamily: "var(--font-mono)", fontSize: 12 }}>{fmtDateTime(t.approvedAt)}</span></Row>
              {t.notes ? <div style={{ gridColumn: "1 / -1" }}><Row label="Not">{t.notes}</Row></div> : null}
            </div>
          </div>

          {/* Talep satırları */}
          <div>
            <div className="mo-sec-title" style={{ fontSize: 12.5, fontWeight: 600, marginBottom: 6 }}>Talep Satırları</div>
            <table className="dt2">
              <thead><tr><th>Ürün</th><th style={{ width: 160 }}>Lot</th><th className="num" style={{ width: 130 }}>Miktar</th></tr></thead>
              <tbody>
                {t.lines.map((l) => (
                  <tr key={l.id}>
                    <td>{l.name}<span style={{ fontSize: 10.5, color: "var(--neutral-400)", marginLeft: 6, fontFamily: "var(--font-mono)" }}>{l.code}</span></td>
                    <td>{l.lotId ? <span className="badge-pill">elle lot</span> : <span className="badge-pill b-info"><Icon name="zap" size={10} />FEFO</span>}</td>
                    <td className="cell-num">{fmtQty(l.qty)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* PENDING: onaylanınca taşınacak lotlar (FEFO önizleme) */}
          {status === "pending" ? (
            <div>
              <div className="mo-sec-title" style={{ fontSize: 12.5, fontWeight: 600, marginBottom: 6 }}>Onaylanınca Taşınacak Lotlar <span style={{ fontWeight: 400, color: "var(--neutral-500)" }}>(güncel FEFO)</span></div>
              {hasShortage ? <div className="form-banner-err" style={{ marginBottom: 8 }}>Bazı satırlarda kaynak depoda yeterli stok yok — bu haliyle onay reddedilir.</div> : null}
              <table className="dt2">
                <thead><tr><th>Ürün</th><th>Çekilecek Lotlar</th><th className="num" style={{ width: 90 }}>İstenen</th><th className="num" style={{ width: 90 }}>Karşılanan</th></tr></thead>
                <tbody>
                  {t.preview.map((p, i) => (
                    <tr key={`${p.itemId}-${i}`}>
                      <td>{p.name}</td>
                      <td>{p.picks.length ? p.picks.map((pk) => `${pk.lotCode} (${fmtQty(pk.qty)})`).join(", ") : <span style={{ color: "var(--danger)" }}>—</span>}</td>
                      <td className="cell-num">{fmtQty(p.requested)}</td>
                      <td className="cell-num" style={p.shortage > 0.0001 ? { color: "var(--danger)", fontWeight: 600 } : undefined}>{fmtQty(p.picked)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}

          {/* APPROVED: fiilen taşınan lotlar */}
          {status === "approved" && t.movements.length ? (
            <div>
              <div className="mo-sec-title" style={{ fontSize: 12.5, fontWeight: 600, marginBottom: 6 }}>Taşınan Lotlar</div>
              <table className="dt2">
                <thead><tr><th>Lot</th><th className="num" style={{ width: 130 }}>Miktar</th></tr></thead>
                <tbody>
                  {t.movements.map((m, i) => (
                    <tr key={`${m.lotCode}-${i}`}><td className="cell-id">{m.lotCode}</td><td className="cell-num">{fmtQty(m.qty)}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
      )}

      <ConfirmDialog open={confirm === "approve"} title="Transferi onayla" tone="primary" confirmLabel="Onayla & Taşı" busy={approveMut.isPending}
        message={<>Stok <b>{t?.fromName}</b> → <b>{t?.toName}</b> deposuna FEFO ile taşınacak. Bu işlem geri alınamaz.{hasShortage ? <div className="form-banner-err" style={{ marginTop: 8 }}>Yetersiz stok var — onay reddedilebilir.</div> : null}</>}
        onConfirm={doApprove} onCancel={() => setConfirm(null)} />
      <ConfirmDialog open={confirm === "cancel"} title="Talebi iptal et" tone="danger" confirmLabel="İptal Et" busy={cancelMut.isPending}
        message={<>Bu iç transfer talebi iptal edilecek. Stok taşınmaz.</>} onConfirm={doCancel} onCancel={() => setConfirm(null)} />
      <HardDeleteConfirm open={delOpen} code={t?.code ?? ""} title="İç transferi kalıcı sil"
        warning={<>Bu iç transfer kaydı <b>kalıcı olarak</b> silinecek. Yalnız onaylanmamış (bekleyen/iptal) kayıtlar silinebilir.</>}
        busy={removeMut.isPending} errorMessage={actErr} onConfirmed={doRemove} onClose={() => setDelOpen(false)} />
    </Drawer>
  );
}
